import { Group, Loader, Radio, Stack, Text, TextInput } from "@mantine/core";
import clsx from "clsx";

import { NarrativeCardActions } from "../NarrativeCardActions/NarrativeCardActions";
import styles from "./SendLinkCard.module.css";

export type SendChannel = "text" | "email";

export interface SendLinkCardProps {
    /** Card title, e.g. "Send product details" (guided browsing) or "Send enrollment". */
    title: string;
    /** Which delivery channel is selected. */
    channel: SendChannel;
    onChannelChange: (channel: SendChannel) => void;
    /** The phone number (text) or email address (email) being sent to. */
    value: string;
    onValueChange: (value: string) => void;
    /** Show the "AI" badge inside the field (suggested value). Default true. */
    showAiBadge?: boolean;
    /**
     * Inline actions (guided-browsing style — Image #4). Provide both to render the in-card action
     * row. Omit for the enrollment step (Image #5), where the footer owns Send.
     */
    onCancel?: () => void;
    cancelLabel?: string;
    onSend?: () => void;
    /** Defaults to "Send text" / "Send email" following the selected channel. */
    sendLabel?: string;
    /** Inline validation message under the field (e.g. bad phone/email). */
    error?: string;
    /** Disable Send (e.g. invalid input). */
    sendDisabled?: boolean;
    /** In-flight send — shows a spinner and disables Send while awaiting the API. */
    sending?: boolean;
}

// Built on Mantine's `Radio.Card` so the pair behaves as one real radio group: a single tab stop plus
// Arrow/Home/End roving focus (ARIA APG), for free, with the LFA pill styling kept via `className`.
const ChannelOption = ({ label, value }: { label: string; value: SendChannel }) => (
    <Radio.Card value={value} className={styles.option}>
        <Group gap={8} align="center" wrap="nowrap">
            <Radio.Indicator size="xs" />
            <span className={styles.optionLabel}>{label}</span>
        </Group>
    </Radio.Card>
);

/**
 * A "send a link" card that renders beneath a beat card within a step (openspec long-form-ai).
 * Two mocked uses: guided browsing ("Send product details", inline actions) in the Transition step,
 * and the enrollment send-link ("Send enrollment", footer-driven) at the end. Presentational and
 * controlled; the step/shell owns the state and the real send.
 */
export const SendLinkCard = ({
    title,
    channel,
    onChannelChange,
    value,
    onValueChange,
    showAiBadge = true,
    onCancel,
    cancelLabel = "No thanks",
    onSend,
    sendLabel,
    error,
    sendDisabled,
    sending,
}: SendLinkCardProps) => {
    const isText = channel === "text";
    const effectiveSendLabel = sendLabel ?? (isText ? "Send text" : "Send email");
    const sendBlocked = sending || sendDisabled;
    return (
        <div className={styles.card}>
            <Stack gap={14}>
                <Text className={styles.title}>{title}</Text>

                <Radio.Group
                    value={channel}
                    onChange={(next) => onChannelChange(next as SendChannel)}
                    // Visually-hidden label names the radiogroup (Mantine wires it via aria-labelledby),
                    // preserving the group's accessible name without adding visible chrome.
                    label="Delivery method"
                    classNames={{ label: styles.srOnly }}
                >
                    <Group grow gap="sm" wrap="nowrap">
                        <ChannelOption label="Text link" value="text" />
                        <ChannelOption label="Email link" value="email" />
                    </Group>
                </Radio.Group>

                <TextInput
                    required
                    label={isText ? "Phone number" : "Email address"}
                    type={isText ? "tel" : "email"}
                    inputMode={isText ? "tel" : "email"}
                    maxLength={isText ? 14 : undefined}
                    placeholder={isText ? "(555) 555-5555" : "name@email.com"}
                    value={value}
                    onChange={(e) => onValueChange(e.currentTarget.value)}
                    error={error}
                    rightSection={showAiBadge ? <span className={styles.aiBadge}>AI</span> : undefined}
                    rightSectionPointerEvents="none"
                    // fs-mask: the field carries a real customer phone/email (AI-suggested), so keep it
                    // out of FullStory session replay — matching product-sale-flow SendLink's input.
                    classNames={{
                        label: styles.fieldLabel,
                        required: styles.req,
                        input: clsx(styles.input, "fs-mask"),
                    }}
                />
            </Stack>

            {(onSend || onCancel) && (
                <NarrativeCardActions>
                    {onSend && (
                        <NarrativeCardActions.Primary onClick={onSend} disabled={sendBlocked}>
                            {sending ? (
                                <Group gap={6} align="center" wrap="nowrap" component="span">
                                    <Loader size={14} color="var(--mantine-color-white)" />
                                    Sending…
                                </Group>
                            ) : (
                                effectiveSendLabel
                            )}
                        </NarrativeCardActions.Primary>
                    )}
                    {onCancel && (
                        <NarrativeCardActions.Link onClick={onCancel} disabled={sending}>
                            {cancelLabel}
                        </NarrativeCardActions.Link>
                    )}
                </NarrativeCardActions>
            )}
        </div>
    );
};
